import {
  ANCHORS,
  ANCHOR_IDS,
  HOLDING_GROUNDS,
  HOLDING_GROUND_IDS,
  KEDGE_USES,
  SCOPE_FORMULA_NOTE,
  SCOPE_GUIDELINES,
  SETTING_PROCEDURE,
  WEIGHING_PROCEDURE,
  type HoldingGround,
} from "@nav-games/anchoring";

import { AnchorFigure } from "../../ui/AnchorFigure";

interface AnchoringProps {
  onNavigate: (to: string) => void;
}

const RATING_COLOUR: Record<HoldingGround["rating"], string> = {
  excellent: "var(--success, #2c7a3b)",
  good: "var(--success, #2c7a3b)",
  moderate: "var(--warn, #d97706)",
  poor: "var(--danger, #b8302a)",
  avoid: "var(--danger, #b8302a)",
};

export function Anchoring({ onNavigate }: AnchoringProps) {
  return (
    <>
      <h2 style={{ marginTop: 0 }}>Anchoring</h2>
      <p className="muted">
        Anchor types, the holding ground they bite in, scope guidance,
        when to use a kedge, and the routine for setting and weighing.
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Play</h3>
        <button onClick={() => onNavigate("/play/anchoring/flashcards")}>
          Identify the anchor — flashcards
        </button>
        <p className="small muted" style={{ margin: "8px 0 16px" }}>
          Show an anchor silhouette, pick the matching name + role from
          four choices. Reveal includes the strengths and weaknesses.
        </p>
        <button
          onClick={() => onNavigate("/play/anchoring/techniques")}
          className="secondary"
        >
          Berthing + anchoring techniques drill
        </button>
        <p className="small muted" style={{ margin: "8px 0 0" }}>
          Scenario → which mooring or anchoring technique fits? Covers
          alongside (with + without springs), fore-and-aft, lazy lines,
          Med stern-to, rafting, trots — plus Bahamian moor, fore-and-aft
          anchors, tandem, kedging, and anchor watch.
        </p>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Scope</h3>
        <p className="small muted" style={{ margin: "0 0 12px" }}>
          {SCOPE_FORMULA_NOTE}
        </p>
        <table style={{ width: "100%", fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: "left" }}>
              <th style={{ padding: "6px 8px 6px 0" }}>Rode</th>
              <th style={{ padding: "6px 8px" }}>Settled</th>
              <th style={{ padding: "6px 8px" }}>Normal</th>
              <th style={{ padding: "6px 8px" }}>Heavy</th>
            </tr>
          </thead>
          <tbody>
            {SCOPE_GUIDELINES.map((s) => (
              <tr key={s.rode}>
                <td style={{ padding: "4px 8px 4px 0", fontWeight: 600 }}>
                  {s.rodeLabel}
                </td>
                <td style={{ padding: "4px 8px" }}>{s.minimum}</td>
                <td style={{ padding: "4px 8px" }}>{s.normal}</td>
                <td style={{ padding: "4px 8px" }}>{s.heavy}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <ul className="small muted" style={{ paddingLeft: 18, marginTop: 12 }}>
          {SCOPE_GUIDELINES.map((s) => (
            <li key={`note-${s.rode}`} style={{ marginBottom: 4 }}>
              <strong>{s.rodeLabel}:</strong> {s.notes}
            </li>
          ))}
        </ul>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Anchor types</h3>
        <p className="small muted" style={{ marginBottom: 12 }}>
          The bower (primary), the kedge (secondary), and a few
          specialists. Most cruising yachts carry a modern scoop on
          the bow, a Fortress/Danforth as a stowable kedge, and
          sometimes a Fisherman for rock or weed.
        </p>
        {ANCHOR_IDS.map((id) => {
          const a = ANCHORS[id]!;
          return (
            <details key={id} className="card" style={{ marginBottom: 8 }}>
              <summary
                style={{
                  cursor: "pointer",
                  listStyle: "none",
                  fontWeight: 600,
                  fontSize: 15,
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <AnchorFigure anchorId={id} size={84} />
                <div style={{ flex: 1 }}>
                  {a.name}
                  <div
                    className="small muted"
                    style={{ marginTop: 2, fontWeight: 400 }}
                  >
                    {a.summary}
                  </div>
                </div>
              </summary>
              <div style={{ marginTop: 12 }}>
                <p className="small muted" style={{ margin: "4px 0" }}>
                  <strong>Role:</strong> {a.role}
                </p>
                <p className="small" style={{ margin: "8px 0 4px" }}>
                  <strong>Strengths</strong>
                </p>
                <ul
                  className="small muted"
                  style={{ paddingLeft: 18, margin: 0 }}
                >
                  {a.strengths.map((s, i) => (
                    <li key={i} style={{ marginBottom: 2 }}>
                      {s}
                    </li>
                  ))}
                </ul>
                <p className="small" style={{ margin: "8px 0 4px" }}>
                  <strong>Watch out for</strong>
                </p>
                <ul
                  className="small muted"
                  style={{ paddingLeft: 18, margin: 0 }}
                >
                  {a.weaknesses.map((w, i) => (
                    <li key={i} style={{ marginBottom: 2 }}>
                      {w}
                    </li>
                  ))}
                </ul>
              </div>
            </details>
          );
        })}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Holding ground</h3>
        <p className="small muted" style={{ marginBottom: 12 }}>
          The chart's seabed abbreviation tells you what you're
          dropping onto. Aim for sand or mud; treat rock and weed
          with suspicion.
        </p>
        <table style={{ width: "100%", fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: "left" }}>
              <th style={{ padding: "6px 8px 6px 0" }}>Type</th>
              <th style={{ padding: "6px 8px" }}>Chart</th>
              <th style={{ padding: "6px 8px" }}>Rating</th>
            </tr>
          </thead>
          <tbody>
            {HOLDING_GROUND_IDS.map((id) => {
              const g = HOLDING_GROUNDS[id]!;
              return (
                <tr key={id} style={{ verticalAlign: "top" }}>
                  <td
                    style={{
                      padding: "6px 8px 6px 0",
                      fontWeight: 600,
                      width: "20%",
                    }}
                  >
                    {g.name}
                  </td>
                  <td className="muted" style={{ padding: "6px 8px", width: 60 }}>
                    {g.chartAbbreviation ?? "—"}
                  </td>
                  <td style={{ padding: "6px 8px" }}>
                    <span
                      style={{ color: RATING_COLOUR[g.rating], fontWeight: 600 }}
                    >
                      {g.rating}
                    </span>
                    <div className="small muted" style={{ marginTop: 2 }}>
                      {g.notes}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>When to use a kedge</h3>
        {KEDGE_USES.map((k) => (
          <div
            key={k.id}
            style={{
              borderTop: "1px solid var(--border)",
              paddingTop: 10,
              marginTop: 10,
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>{k.title}</p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>When:</strong> {k.scenario}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>How:</strong> {k.method}
            </p>
          </div>
        ))}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Setting the anchor</h3>
        <ol className="small" style={{ paddingLeft: 18, margin: 0 }}>
          {SETTING_PROCEDURE.map((s, i) => (
            <li key={i} style={{ marginBottom: 6 }}>
              {s.text}
            </li>
          ))}
        </ol>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Weighing the anchor</h3>
        <ol className="small" style={{ paddingLeft: 18, margin: 0 }}>
          {WEIGHING_PROCEDURE.map((s, i) => (
            <li key={i} style={{ marginBottom: 6 }}>
              {s.text}
            </li>
          ))}
        </ol>
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/reference")}>
        ← Back to Reference
      </button>
    </>
  );
}
